/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CaptureHeader from '../CaptureHeader';

// Import custom icons
import cameraWhiteIcon from '../../assets/camera-white.svg';
import cameraIcon from '../../assets/camera.svg';
import videoWhiteIcon from '../../assets/video-white.svg';
import videoIcon from '../../assets/video.svg';
import scannerWhiteIcon from '../../assets/scanner-white.svg';
import scannerIcon from '../../assets/scanner.svg';
import filesWhiteIcon from '../../assets/files-white.svg';
import filesIcon from '../../assets/files.svg';
import ReferenceTooltip from './ReferenceTooltip';
import './capture.css';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';

const Instructions = ({
  assignment,
  items,
  branding,
  onStart,
  onBack,
  tenant,
  workflow,
  pageBgStyle,
  welcomeBgClass
}) => {
  const currentTheme = branding.theme || 'light';
  const isDark = ['dark', 'synthwave', 'halloween', 'forest', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);
  let instructionsMainTitle = "What you'll need to capture";
  if (workflow.branding.instructionsMainTitle) {
    instructionsMainTitle = workflow.branding.instructionsMainTitle;
  }

  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  return (
    <div
      data-theme={currentTheme}
      className="min-h-screen flex flex-col font-sans transition-colors duration-500 bg-base-100 text-base-content overflow-x-hidden pt-[env(safe-area-inset-top,20px)] capture-container"
    >
      {/* Top Header Row */}
      <div className="w-full max-w-lg mx-auto">
        <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow.branding?.logoUrl} branding={branding} type='before-capture' />
      </div>

      <div className="flex-1 px-3 pb-32 w-full max-w-lg mx-auto flex flex-col">
        {/* Assignment Info */}
        <div className="mb-8 flex flex-col gap-1">
          <h1 className="text-[20px] font-bold tracking-tight mb-0 text-base-content leading-none custom-line-break">
            {assignment.submitterName}
          </h1>
          <div className="flex flex-col gap-1.5 text-[14px]">
            <div className="flex justify-between items-start overflow-visible custom-line-break w-full">
              <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
              <span className="relative inline-block text-right shrink-0">
                <button
                  type="button"
                  ref={tooltipTriggerRef}
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-primary underline decoration-dashed focus:outline-none custom-line-break"
                >
                  {assignment.customerReferenceNumber}
                </button>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </span>
            </div>
          </div>
        </div>

        {/* Section Title */}
        <div className="mb-8">
          <h2 className="text-[20px] font-bold tracking-tight mb-2 custom-line-break">{instructionsMainTitle}</h2>
          <p className="text-[14px] leading transition-opacity">
            Follow each step. You&apos;ll be able to review all media before final submission.
          </p>
        </div>

        {/* Steps List */}
        <div className="space-y-7">

          {items.map((item, index) => {
            const isVideo = item.type === 'video' || item.description?.toLowerCase().includes('video');

            const isIdentifier = item.type === 'identifier';

            const isFile = item.type === 'file'

            let IconAsset;

            if (isIdentifier) {
              IconAsset = isDark ? scannerWhiteIcon : scannerIcon;
            } else if (isVideo) {
              IconAsset = isDark ? videoWhiteIcon : videoIcon;
            } else if(isFile) {
              IconAsset = isDark ? filesWhiteIcon : filesIcon;
            } else {
              IconAsset = isDark ? cameraWhiteIcon : cameraIcon;
            }

            return (
              <div key={item.id || item.assignmentItemId} className="flex items-start gap-6 ps-4">
                <div className="flex-shrink-0 pt-0">
                  {typeof IconAsset === 'string' ? (
                    <img src={IconAsset} alt="" className={`w-10 h-10 object-contain ${isFile ? 'p-1' : ''}`} />
                  ) : (
                    <IconAsset size={38} strokeWidth={1.5} className="text-base-content" />
                  )}
                </div>
                <div className="flex flex-col flex-1">
                  <p className="text-[16px] font-bold leading-tight mb-1">Step {index + 1}</p>
                  <p className="text-[14px] leading-snug custom-line-break">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fixed Sticky Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-base-100 px-3 pt-3 pb-[calc(env(safe-area-inset-bottom,16px)+8px)] w-full max-w-lg mx-auto flex flex-col z-20">
        <div className="w-full max-w-lg mx-auto flex justify-between">
          <button
            onClick={onBack}
            className="btn btn-primary font-semibold text-[20px] rounded-[8px] shadow-xl transition-all active:scale-95 border border-primary/20 w-[90px] h-[40px] min-h-[40px] leading-none"
          >
            Back
          </button>
          <button
            onClick={onStart}
            className="btn btn-primary font-semibold text-[20px] rounded-[8px] shadow-xl transition-all active:scale-95 border border-primary/20 w-[90px] h-[40px] min-h-[40px] leading-none"
          >
            Start
          </button>
        </div>
      </div>
    </div>
  );
};

export default Instructions;

Instructions.propTypes = {
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  onStart: PropTypes.func,
  onBack: PropTypes.func,
  tenant: PropTypes.any,
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      instructionsMainTitle: PropTypes.string,
      logoUrl: PropTypes.string,
    }),
  }),
  pageBgStyle: PropTypes.any,
  welcomeBgClass: PropTypes.string,
};
